/** @require: var Keyboard = require("@webhare-system/dom/keyboard")
*/

/* Translation guide from mootools keyboard.js to keyboard-bunny.js:

  new Keyboard( { events: {"up":.... }})
  =>
  new KeyboardHandler(node, {"up":....})

  In event handlers:

  Keyboard.stop => event.preventDefault();
  event.stop => event.preventDefault(); event.stopPropagation();

  or just add a {stopmapped:true} to the KeyboardHandler

  event.shift => event.shiftKey
  event.ctrl => event.controlKey
  event.meta => event.metaKey
  event.alt => event.altKey

  All keynames should be converted to mixed-cased, as done here: http://www.w3.org/TR/DOM-Level-3-Events-key/

  Modifiers should be in the ordering Shift+Control+Alt+Meta+key

  To simply prevent keys from propagating up (ie to keep Enter inside its textarea)
  new KeyboardHandler(this.textarea, {}, { dontpropagate: ['Enter']});
  */

import * as dompack from 'dompack';

// We standardize on the key values here: http://www.w3.org/TR/DOM-Level-3-Events-key/
var remap = { "U+0008": "Backspace"
            , "U+0009": "Tab"
            , "U+001B": "Escape"
            , "U+007F": "Delete"
            , "Up": "ArrowUp"
            , "Down": "ArrowDown"
            , "Left": "ArrowLeft"
            , "Right": "ArrowRight"
            , "Esc": "Escape"
            };
var remapkeycode = { 48:'0', 49: '1', 50: '2', 51: '3', 52: '4', 53: '5', 54: '6', 55: '7', 56: '8', 57: '9'
                   , 65:'A', 66: 'B', 67: 'C', 68: 'D', 69: 'E', 70: 'F', 71: 'G', 72: 'H', 73: 'I', 74: 'J'
                   , 75:'K', 76: 'L', 77: 'M', 78: 'N', 79: 'O', 80: 'P', 81: 'Q', 82: 'R', 83: 'S', 84: 'T'
                   , 85:'U', 86: 'V', 87: 'W', 88: 'X', 89: 'Y', 90: 'Z'};
var propnames = { "shiftKey":   "Shift"
                , "ctrlKey":    navigator.platform == "MacIntel" ? "Control" : [ "Accel", "Control" ]
                , "metaKey":    navigator.platform == "MacIntel" ? [ "Accel", "Meta" ] : "Meta"
                , "altKey":     "Alt"
                };

var selenium_backup =
    { 8: "Backspace", 9: "Tab"
    , 13: "Enter", 27: "Escape"
    , 33: "PageUp", 34: "PageDown"
    , 35: "End", 36: "Home"
    , 37: "ArrowLeft", 38: "ArrowUp", 39: "ArrowRight", 40: "ArrowDown"
    , 45: "Insert", 46: "Delete"
    , 112: "F1", 113: "F2", 114: "F3", 115: "F4", 116: "F5", 117: "F6", 118: "F7", 119: "F8", 120: "F9", 121: "F10"
    };

function getKeyNames(event)
{
  let names = [[]];

/*
  // Firefix under selenium on linux always says 'Unidentified' as key. Backup for some keys.
  if (basekey == "Unidentified")
    basekey = selenium_backup[event.keyCode];
*/
  // Create the modifiers in the names array (omit the basekey, so we can sort on modifier first)
  Object.keys(propnames).forEach(function(propname)
  {
    if (event[propname])
    {
      // The key is pressed. Add the modifier name to all current names.
      var modifier = propnames[propname];
      if (!Array.isArray(modifier))
        names.forEach(function(arr) { arr.append([modifier]); });
      else
      {
        // Multiple modifiers map to this key, duplicate all result sequences for every modifier
        var newkeys = [];
        modifier.forEach(function(singlemodifier)
        {
          names.forEach(function(arr)
          {
            newkeys.push(arr.concat([ singlemodifier ]));
          });
        });
        names = newkeys;
      }
    }
  });

  names = names.map(function(arr)
  {
    // Sort the modifier names
    arr = arr.sort();
    arr.push(event.key.length === 1 ? event.key.toUpperCase() : event.key);
    return arr.join("+");
  });

  return names;
}

function validateKeyName(key)
{
  var modifiers = key.split("+");
  modifiers.pop();

  // Check for allowed modifiers
  modifiers.forEach(function(mod)
  {
    if (![ "Accel", "Alt", "Control", "Meta", "Shift"].contains(mod))
      throw new Error("Illegal modifier name '" + mod + "' in key '" + key + "'");
  });

  var original_order = modifiers.join('+');
  modifiers.sort();
  if (modifiers.join('+') != original_order)
    throw new Error("Illegal key name " + key + ", modifiers must be sorted alphabetically");
}

/** node: The node to attach to
    keymap: Keymap
    options.stopmapped - preventDefault and stopPropagation on any key we have in our map
    options.dontpropagate - string array of keys not to propagate out of this object
    options.onkeypress - when set, call for all keypresses. signature: function(event, key). Return false to stop
      propagation and default action.
*/
export default class KeyboardHandler
{
  constructor(node, keymap, options)
  {
    this.node = node;
    this.keymap = {};
    this.stopmapped = options&&options.stopmapped;
    this.dontpropagate = options && options.dontpropagate ? [...options.dontpropagate].map(name => name.toUpperCase()) : [];
    this.onkeypress = options&&options.onkeypress;
    this.captureunsafekeys = options&&options.captureunsafekeys;

    Object.keys(keymap).forEach(keyname =>
    {
      if (dompack.debugflags.key)
        validateKeyName(keyname);
      this.keymap[keyname.toUpperCase()] = keymap[keyname];
    });

    node.addEventListener('keydown',  (event) => this._onKeyDown(this,event) );
    node.addEventListener('keypress', (event) => this._onKeyPress(this,event) );
    node.addEventListener('keyup',    (event) => this._onKeyUp(this,event) );
  }

  /** Returns thether the current pressed special key should be ignored for the current target node
      Used to detect input/textarea/rte's
      @param target Current target node for keyboard event
      @param key Parsed key (as returned by GetKeyNames)
      @return Whether the key must be ignored by KeyboardHandler, default browser behaviour should be triggered.
  */
  _mustIgnoreKey(target, key, keynames)
  {
    var tag = target.nodeName.toLowerCase();
    if (tag == "select")
    {
      if (["ArrowUp", "ArrowDown",  "Home", "End", "PageUp", "PageDown"].indexOf(key) != -1)
        return true;
    }
    else if (tag == "input" || tag == "textarea" || target.isContentEditable)
    {
      // These keys we ignore, regardless of the modifier
      if ([ "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"
          , "PageUp", "PageDown"
          , "Home", "End"
          , "Insert", "Delete", "Backspace"
          ].indexOf(key) != -1)
        return true;

      var is_special_combo = false;

      // only input doesn't want exact combo 'Enter', the rest does
      if (tag != "input" && keynames.indexOf("Enter") != -1)
        is_special_combo = true;

      // Only contenteditable wants "Shift+Enter"
      if (target.isContentEditable && keynames.indexOf("Shift+Enter") != -1)
        is_special_combo = true;

      // These exact combo's are wanted by all inputs
      [ "Accel-A", "Accel-V", "Accel-C", "Accel-X" ].forEach(function(name)
      {
        is_special_combo = is_special_combo || keynames.indexOf(name) != -1;
      });
      return is_special_combo;
    }
    return false;
  }

  addKey(keybinding, handler)
  {
    if(dompack.debugflags.key)
    {
      validateKeyName(keybinding);
      console.log("[key] KeyDown handler registered for " + keybinding);
    }
    this.keymap[keybinding.toUpperCase()] = handler;
  }
  removeKey(keybinding)
  {
    delete this.keymap[keybinding.toUpperCase()];
  }
  _onKeyDown(node,event)
  {
    let keydata = dompack.normalizeKeyboardEventData(event);

    // Get all possible names for this key
    let keynames = getKeyNames(keydata);
    if (!keydata.key || !keynames.length)
    {
      if(dompack.debugflags.key)
        console.log("[key] KeyDown handler for ", this.node, " did not recognize key from event",event);
      return true;
    }

    if(dompack.debugflags.key)
      console.log("[key] KeyDown handler for ", this.node, " got key ", keydata.key, " with target ", event.target, " keynames:",keynames);

    /* Some keys we ignore, unless we're explicitly bound to a node, so we don't inadvertly break eg a <input> node inside
       a listview we're handling or otherwise break a user's expectation. Set the option 'captureunsafekeys' if you explicitly
       want to be able to capture any key */

    if (!this.captureunsafekeys && this._mustIgnoreKey(event.target, keydata.key, keynames))
    {
      if(dompack.debugflags.key)
        console.log("[key] KeyDown event will not be intercepted, it's an unsafe key to intercept");
      return true;
    }

    if (this.dontpropagate)
    {
      keynames.forEach(keyname =>
      {
        if (this.dontpropagate.contains(keyname))
        {
          if(dompack.debugflags.key)
            console.log("[key] KeyDown event will not bubbleup because of our dontpropagate option (but may still trigger a default action)");
          event.stopPropagation();
        }
      });
    }

    for (var i = 0; i < keynames.length; ++i)
    {
      let mapping = this.keymap[keynames[i].toUpperCase()];
      if(!mapping)
        continue;

      if (this.stopmapped)
      {
        if(dompack.debugflags.key)
          console.log("[key] KeyDown event will not bubbleup or trigger default, because we're configured to block any mapped key");
        event.stopPropagation();
        event.preventDefault();
      }

      let ishandled = mapping.apply(node,[event]);
      if(ishandled && !this.stopmapped)
      {
        event.stopPropagation();
        event.preventDefault();
        if(dompack.debugflags.key)
          console.log("[key] KeyDown event will not bubbleup or trigger default, because the keyhandler indicated the key was handled");
      }

      if(!event.defaultPrevented && dompack.debugflags.key)
        console.log("[key] KeyDown event was not blocked by its explicitly configured handler");
    }
    return true;
  }
  _onKeyPress(node,event)
  {
    let keydata = dompack.normalizeKeyboardEventData(event);

    if (this.onkeypress)
    {
      if (!this.onkeypress.apply(node, [ event, keydata.key ]))
      {
        event.stopPropagation();
        event.preventDefault();
        return false;
      }
    }

    return true;
  }

  _onKeyUp(node,event)
  {
    return true;
  }
}

export function getEventKeyNames(event)
{
  let keydata = dompack.normalizeKeyboardEventData(event);
  return getKeyNames(keydata);
}

/** Is the native 'copy' modifier for this platform pressed? */
KeyboardHandler.hasNativeEventCopyKey = function(event)
{
  return event && (navigator.platform == "MacIntel" ? event.altKey : event.ctrlKey);
};

/** Is the native 'multiselect' modifier for this platform pressed? */
KeyboardHandler.hasNativeEventMultiSelectKey = function(event)
{
  return event && (navigator.platform == "MacIntel" ? event.metaKey : event.ctrlKey);
};



// WEBPACK FOOTER //
// /opt/wh/whtree/~/dompack/extra/keyboard.es